import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Loading } from '@bumble/app/components/Loading';

interface UnassignSharedDataPoolProps {
  cpeId: string;
  onSubmit: () => void;
}

type CpeWithDataPool = {
  cpe_id: string;
  data_pool_id?: string;
  data_pool_name?: string;
};

export const UnassignSharedDataPool = ({ cpeId, onSubmit }: UnassignSharedDataPoolProps) => {
  const api = useApi();

  const { isLoading: loadingCpes, data: cpeData } = useQuery(
    ['cellularRouters'],
    api.getCpeCellularApiV1GetCpeCellularGet.bind(null, {}),
    { enabled: true },
  );

  const { isLoading: loadingPools, data: dataPools } = useQuery(
    ['dataPools'],
    api.getDataPoolApiV1GetDataPoolGet.bind(null, {}),
    { enabled: true },
  );

  const currentPoolName = useMemo(() => {
    console.log(cpeData);
    const cpe = cpeData?.data.find((item) => item.cpe_id === cpeId) as CpeWithDataPool | undefined;
    if (cpe?.data_pool_name) return cpe.data_pool_name;
    if (cpe?.data_pool_id) {
      return dataPools?.data.find((pool) => pool.data_pool_id === cpe.data_pool_id)?.data_pool_name
        ?? cpe.data_pool_id;
    }
    return 'Not assigned';
  }, [cpeData, cpeId, dataPools]);

  const unassignDataPool = useMutation(api.removeCpeFromDataPoolApiV1RemoveCpeFromDataPoolPost, {
    onSuccess() {
      toast.success('Unassigned Shared Data Pool successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    unassignDataPool.mutate({ cpe_id: cpeId });
  };

  if (loadingCpes || loadingPools) return <Loading />;

  return (
    <Box pt={2}>
      <Typography sx={{ mb: 3 }}>
        Remove this CPE from its assigned Shared Data Pool.
      </Typography>
      <TextField
        fullWidth
        label="Shared Data Pool"
        variant="outlined"
        value={currentPoolName}
        disabled
      />
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={currentPoolName === 'Not assigned' || unassignDataPool.isLoading}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

UnassignSharedDataPool.displayName = 'Unassign Shared Data Pool';
